import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';

const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [countdown, setCountdown] = useState(5);
  const lang = i18n.language;

  useEffect(() => {
    if (countdown <= 0) {
      navigate('/mypage/profile', { replace: true });
      return;
    }
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown, navigate]);

  const handleManualSkip = () => {
    navigate('/mypage/profile', { replace: true });
  };

  const isZh = lang.toLowerCase().startsWith('zh');
  
  const content = {
    title: isZh ? '欢迎开启 Melodis 之旅' : 'Melodis Clinic へようこそ',
    subtitle: isZh ? '您的账号已成功创建' : 'アカウントが正常に作成されました',
    description: isZh 
      ? '为了给您提供安全、合规的高品质在线医疗服务，请先花费 1 分钟完善您的基本个人资料。这有助于医师为您做出更准确的诊断。' 
      : '安全で高品質なオンライン診療を提供するため、まずはプロフィール（診療用基本情報）のご入力をお願いいたします。',
    redirecting: isZh ? '秒后自动进入设置' : '秒後に設定画面へ移動します',
    button: isZh ? '立即完善资料' : 'プロフィールを入力する',
    brand: 'Melodis Clinic'
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-coral/5 blur-[120px] rounded-full" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-navy/5 blur-[120px] rounded-full" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="max-w-xl w-full"
      >
        <div className="bg-white/80 backdrop-blur-xl rounded-[40px] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.08)] border border-white p-8 md:p-14 text-center relative overflow-hidden">
          
          {/* Logo Section */}
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="flex justify-center mb-10"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-coral/10 blur-2xl rounded-full scale-150" />
              <img 
                src="/images/Melodislogo.png" 
                alt="Melodis Clinic" 
                className="h-16 md:h-20 w-auto relative z-10"
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/200x80?text=Melodis+Clinic';
                }}
              />
            </div>
          </motion.div>

          {/* Success Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
            className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-1.5 rounded-full text-xs font-black tracking-wider uppercase mb-6"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {content.subtitle}
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-3xl md:text-4xl font-black text-navy mb-6 tracking-tight leading-tight"
          >
            {content.title}
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-slate-500 text-sm md:text-base leading-relaxed mb-10 font-medium px-4"
          >
            {content.description}
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="space-y-6"
          >
            <button
              onClick={handleManualSkip}
              className="w-full bg-gradient-to-r from-navy to-[#2A3F6F] text-white py-5 px-8 rounded-2xl flex items-center justify-center gap-3 transition-all transform hover:scale-[1.02] hover:shadow-2xl shadow-navy/20 font-black text-base group"
            >
              {content.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <div className="flex items-center justify-center gap-3 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin text-coral" />
              <span className="text-xs md:text-sm font-bold tracking-wide">
                <span className="text-navy font-black tabular-nums">{countdown}</span> {content.redirecting}
              </span>
            </div>
          </motion.div>

          {/* Progress Indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100/50">
            <motion.div 
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 5, ease: "linear" }}
              className="h-full bg-gradient-to-r from-coral to-rose-400"
            />
          </div>
        </div>

        <p className="text-center mt-8 text-slate-300 text-xs font-bold tracking-widest uppercase">
          © {new Date().getFullYear()} {content.brand}
        </p>
      </motion.div>
    </div>
  );
};

export default Welcome;
